import React from "react";
import { Box, Chip, Paper, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import {
	SERVICE_WINDOW_COLORS,
	buildMonthMarkers,
	formatServiceDate,
	getBarMetrics,
	getTimelineRange,
	getTodayMarkerPercent,
} from "../../utils/serviceWindow";

const LABEL_WIDTH = 220;
const CHART_MIN_WIDTH = 900;

const ServiceWindowGantt = ({ visitTypes, academicYear, onVisitTypeClick }) => {
	const timeline = getTimelineRange(visitTypes, academicYear);
	const monthMarkers = buildMonthMarkers(timeline);
	const todayPercent = getTodayMarkerPercent(timeline);

	const chartVisitTypes = (visitTypes || []).filter(
		(visitType) => visitType.start_date && visitType.end_date
	);

	return (
		<Paper variant="outlined" sx={{ overflow: "auto" }}>
			<Box sx={{ minWidth: CHART_MIN_WIDTH, p: 2 }}>
				<Box sx={{ display: "flex", mb: 1, pl: `${LABEL_WIDTH}px` }}>
					<Box sx={{ position: "relative", flex: 1, height: 28 }}>
						{monthMarkers.map((marker) => (
							<Typography
								key={marker.key}
								variant="caption"
								color="text.secondary"
								sx={{
									position: "absolute",
									left: `${marker.leftPercent}%`,
									transform: "translateX(-50%)",
									whiteSpace: "nowrap",
								}}>
								{marker.label}
							</Typography>
						))}
					</Box>
				</Box>

				{chartVisitTypes.length === 0 ? (
					<Box sx={{ py: 6, textAlign: "center" }}>
						<Typography color="text.secondary">
							No visit types with start and end dates configured yet.
						</Typography>
					</Box>
				) : (
					chartVisitTypes.map((visitType, index) => {
						const metrics = getBarMetrics(visitType, timeline);
						const color = SERVICE_WINDOW_COLORS[index % SERVICE_WINDOW_COLORS.length];

						return (
							<Box
								key={visitType.id}
								sx={{
									display: "flex",
									alignItems: "center",
									mb: 1.5,
									minHeight: 48,
								}}>
								<Box
									sx={{
										width: LABEL_WIDTH,
										pr: 2,
										flexShrink: 0,
										cursor: "pointer",
									}}
									onClick={() => onVisitTypeClick?.(visitType)}>
									<Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
										{visitType.name}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{formatServiceDate(visitType.start_date)} –{" "}
										{formatServiceDate(visitType.end_date)}
									</Typography>
									<Chip
										label={visitType.visit_type || "Service"}
										size="small"
										sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
									/>
								</Box>

								<Box
									sx={{
										position: "relative",
										flex: 1,
										height: 36,
										bgcolor: "grey.100",
										borderRadius: 1,
										border: "1px solid",
										borderColor: "divider",
									}}>
									{todayPercent !== null && (
										<Box
											sx={{
												position: "absolute",
												left: `${todayPercent}%`,
												top: -4,
												bottom: -4,
												width: "2px",
												bgcolor: "error.main",
												zIndex: 2,
											}}
										/>
									)}

									{metrics && (
										<Tooltip
											title={`${visitType.name}: ${formatServiceDate(
												visitType.start_date
											)} to ${formatServiceDate(visitType.end_date)}`}>
											<Box
												onClick={() => onVisitTypeClick?.(visitType)}
												sx={{
													position: "absolute",
													left: `${metrics.leftPercent}%`,
													width: `${Math.max(metrics.widthPercent, 1.5)}%`,
													top: 6,
													bottom: 6,
													bgcolor: color,
													borderRadius: 1,
													cursor: "pointer",
													opacity: metrics.isActive ? 1 : 0.72,
													border: metrics.isActive ? "2px solid #111827" : "none",
													display: "flex",
													alignItems: "center",
													px: 1,
													overflow: "hidden",
													"&:hover": {
														filter: "brightness(0.95)",
													},
												}}>
												<Typography
													variant="caption"
													sx={{
														color: "common.white",
														fontWeight: 600,
														whiteSpace: "nowrap",
														overflow: "hidden",
														textOverflow: "ellipsis",
													}}>
													{visitType.name}
												</Typography>
											</Box>
										</Tooltip>
									)}
								</Box>
							</Box>
						);
					})
				)}

				{todayPercent !== null && (
					<Box sx={{ display: "flex", alignItems: "center", mt: 2, pl: `${LABEL_WIDTH}px` }}>
						<Box
							sx={{
								width: 12,
								height: 12,
								bgcolor: "error.main",
								borderRadius: "50%",
								mr: 1,
							}}
						/>
						<Typography variant="caption" color="text.secondary">
							Today ({dayjs().format("DD MMM YYYY")})
						</Typography>
					</Box>
				)}
			</Box>
		</Paper>
	);
};

export default ServiceWindowGantt;
